/**
 * PageList.jsx
 * 
 * Left sidebar showing all pages as draggable thumbnails.
 * Uses react-window for virtualization (performance with 100+ pages).
 * 
 * Features:
 * - Drag-and-drop reordering
 * - Visual feedback for selected/dragging pages
 * - Lock indicator for cover/TOC pages
 * - Page type icons
 */

import React, { useCallback, useRef } from 'react';
import { useEditor } from './EditorContext';
import PageThumbnail from './PageThumbnail';
import {
  GripVertical,
  Lock,
  BookOpen,
  List as ListIcon,
  Image,
  Star,
  Users,
  Heart,
  FileText
} from 'lucide-react';

// Page type to icon mapping
const PAGE_ICONS = {
  'cover': BookOpen,
  'toc': ListIcon,
  'section': FileText,
  'content': Image,
  'milestone': Star,
  'family-tree': Users,
  'birth-story': Heart,
  'blank': FileText,
};

export default function PageList({ pages }) {
  const { state, selectPage, reorderPages, setDragging } = useEditor();
  const listRef = useRef(null);
  const draggedIndexRef = useRef(null);
  const dragOverIndexRef = useRef(null);
  
  // Handle drag start
  const handleDragStart = useCallback((e, index) => {
    const page = pages[index];
    if (page.isLocked) {
      e.preventDefault();
      return;
    }
    
    draggedIndexRef.current = index;
    setDragging(true);
    
    // Set drag image
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    
    // Add dragging class after a frame (for CSS transitions)
    requestAnimationFrame(() => {
      e.target.classList.add('opacity-50');
    });
  }, [pages, setDragging]);
  
  // Handle drag over
  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (dragOverIndexRef.current !== index) {
      dragOverIndexRef.current = index;
      // Could add visual indicator here
    }
  }, []);
  
  // Handle drop
  const handleDrop = useCallback((e, dropIndex) => {
    e.preventDefault();
    
    const dragIndex = draggedIndexRef.current;
    if (dragIndex === null || dragIndex === dropIndex) {
      return;
    }
    
    // Don't allow dropping on locked pages
    const targetPage = pages[dropIndex];
    if (targetPage.isLocked) {
      return;
    }
    
    reorderPages(dragIndex, dropIndex);
  }, [pages, reorderPages]);
  
  // Handle drag end
  const handleDragEnd = useCallback((e) => {
    draggedIndexRef.current = null;
    dragOverIndexRef.current = null;
    setDragging(false);
    e.target.classList.remove('opacity-50');
  }, [setDragging]);
  
  // Calculate list height
  const listHeight = typeof window !== 'undefined' 
    ? window.innerHeight - 200 // Account for toolbar and header
    : 600;
  
  // Render individual page item
  const PageItem = useCallback(({ index, style }) => {
    const page = pages[index];
    const isSelected = state.selectedPageId === page.id;
    const Icon = PAGE_ICONS[page.type] || Image;
    
    return (
      <div
        style={style}
        className="px-2 py-1"
      >
        <div
          draggable={!page.isLocked}
          onDragStart={(e) => handleDragStart(e, index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDrop={(e) => handleDrop(e, index)}
          onDragEnd={handleDragEnd}
          onClick={() => selectPage(page.id)}
          className={`
            relative flex items-center gap-2 p-2 rounded-lg cursor-pointer
            transition-all duration-150 ease-out
            ${isSelected 
              ? 'bg-ocean-100 ring-2 ring-ocean-500' 
              : 'bg-white hover:bg-gray-50 border border-gray-200'
            }
            ${page.isLocked ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'}
          `}
        >
          {/* Drag handle */}
          {!page.isLocked && (
            <div className="text-gray-400 hover:text-gray-600">
              <GripVertical className="w-4 h-4" />
            </div>
          )}
          
          {/* Lock icon for locked pages */}
          {page.isLocked && (
            <div className="text-gray-300">
              <Lock className="w-4 h-4" />
            </div>
          )}
          
          {/* Page thumbnail preview */}
          <div className="flex-shrink-0 w-12 h-16 bg-gray-100 rounded border border-gray-200 flex items-center justify-center overflow-hidden">
            <PageThumbnail page={page} mini />
          </div>
          
          {/* Page info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <Icon className="w-3 h-3 text-gray-400" />
              <span className="text-xs text-gray-500 truncate">
                {getPageLabel(page, index)}
              </span>
            </div>
            {page.sectionTitle && (
              <p className="text-sm font-medium text-gray-700 truncate">
                {page.sectionTitle}
              </p>
            )}
            <p className="text-xs text-gray-400">
              {getLayoutLabel(page.layout)}
            </p>
          </div>
          
          {/* Page number */}
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-gray-100 rounded text-xs text-gray-500">
            {index + 1}
          </div>
        </div>
      </div>
    );
  }, [pages, state.selectedPageId, handleDragStart, handleDragOver, handleDrop, handleDragEnd, selectPage]);
  
  // Simple list without virtualization
  return (
    <div className="flex-1 overflow-auto">
      {pages.map((page, index) => (
        <div key={page.id} style={{ height: 88 }}>
          <PageItem index={index} style={{}} />
        </div>
      ))}
    </div>
  );
}

// ============================================
// HELPER FUNCTIONS
// ============================================

function getPageLabel(page, index) {
  const labels = {
    'cover': 'Cover',
    'toc': 'Contents',
    'section': 'Section',
    'content': 'Page',
    'milestone': 'Milestone',
    'family-tree': 'Family Tree',
    'birth-story': 'Birth Story',
    'blank': 'Blank',
  };
  return labels[page.type] || `Page ${index + 1}`;
}

function getLayoutLabel(layout) {
  const labels = {
    'hero': '1 Photo',
    'hero-text': '1 Photo + Text',
    'grid-2': '2 Photos',
    'grid-3': '3 Photos',
    'grid-4': '4 Photos',
    'text-left': 'Text Left',
    'text-right': 'Text Right',
    'text-only': 'Text Only',
    'full-bleed': 'Full Bleed',
    'collage': 'Collage',
  };
  return labels[layout] || layout;
}
